import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  apiRecipientCreate,
  apiRecipientOptions,
  apiCountriesIndex,
} from "../lib/api";
import type { Country } from "../lib/api";
import { validateIbanRemote } from "../lib/iban";

type Step = "select" | "sepa" | "swift";
type Kind = "individual" | "business";
type ApiValidation = Record<string, string[]>;

export default function RecipientCreate() {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("select");
  const [recipientKind, setRecipientKind] = useState<Kind>("individual");

  const [opts, setOpts] = useState<{
    allowMultiCurrency: boolean;
    defaultCurrency: string;
    enabledCurrencies: string[];
  }>({
    allowMultiCurrency: false,
    defaultCurrency: "EUR",
    enabledCurrencies: [],
  });

  const [countries, setCountries] = useState<Country[]>([]);

  // shared fields
  const [recipient_name, setName] = useState("");
  const [currency, setCurrency] = useState("");

  // sepa fields
  const [iban, setIban] = useState("");
  const [ibanValid, setIbanValid] = useState<boolean | null>(null);
  const [ibanMsg, setIbanMsg] = useState("");

  // swift fields
  const [full_address, setAddress] = useState("");
  const [account_number, setAccount] = useState("");
  const [bank_bic, setBic] = useState("");
  const [routing_number, setRouting] = useState("");
  const [bank_name, setBankName] = useState("");
  const [bank_address, setBankAddress] = useState("");
  const [bank_country, setBankCountry] = useState(""); // ISO-2 from dropdown
  const [intermediary_bank_name, setInterName] = useState("");
  const [intermediary_bank_bic, setInterBic] = useState("");
  const [ibanOpt, setIbanOpt] = useState("");

  // swift IBAN validation (optional field)
  const [swiftIbanValid, setSwiftIbanValid] = useState<boolean | null>(null);
  const [swiftIbanMsg, setSwiftIbanMsg] = useState("");

  // errors
  const [formError, setFormError] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<ApiValidation>({});

  // ref to the address input for Google Places
  const addressInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    (async () => {
      const [o, list] = await Promise.all([
        apiRecipientOptions(),
        apiCountriesIndex(),
      ]);
      setOpts(o);
      setCountries(list || []);
      setCurrency(o.defaultCurrency || "EUR");
    })();
  }, []);

  async function checkIban() {
    if (!iban) {
      setIbanValid(null);
      setIbanMsg("");
      return;
    }
    setIbanMsg("Validating IBAN...");
    const res = await validateIbanRemote(iban);
    if (res.valid) {
      setIbanValid(true);
      setIbanMsg(`✅ Valid IBAN ${res.country ? `(${res.country})` : ""}`);
    } else {
      setIbanValid(false);
      setIbanMsg(
        `❌ Invalid IBAN: ${res.message ?? "Please check the format."}`
      );
    }
  }

  async function checkSwiftIban() {
    const raw = ibanOpt.trim();
    if (!raw) {
      // optional: empty is fine
      setSwiftIbanValid(null);
      setSwiftIbanMsg("");
      return;
    }

    setSwiftIbanMsg("Validating IBAN...");
    const res = await validateIbanRemote(raw);
    if (res.valid) {
      setSwiftIbanValid(true);
      setSwiftIbanMsg(
        `✅ Valid IBAN ${res.country ? `(${res.country})` : ""}`
      );
    } else {
      setSwiftIbanValid(false);
      setSwiftIbanMsg(
        `❌ Invalid IBAN: ${res.message ?? "Please check the format."}`
      );
    }
  }

  // ---- UI helpers (consistent, comfy spacing) ----
  const inputCls =
    "w-full border border-gray-300 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 " +
    "focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition";
  const labelCls = "block font-medium text-gray-700 text-sm";
  const fieldWrap = "space-y-2";

  function effectiveCurrency(): string {
    // Always lock to server default; fallback to state/EUR
    return (opts.defaultCurrency || currency || "EUR").toUpperCase();
  }

  // -------- Google Places Autocomplete for SWIFT address --------
  useEffect(() => {
    if (step !== "swift") return;

    const w = window as any;
    if (
      !w.google ||
      !w.google.maps ||
      !w.google.maps.places ||
      !addressInputRef.current
    ) {
      // Script not loaded yet or ref missing
      return;
    }

    const autocomplete = new w.google.maps.places.Autocomplete(
      addressInputRef.current,
      {
        types: ["geocode"],
        fields: ["formatted_address"],
      }
    );

    const listener = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      const formatted =
        place?.formatted_address || addressInputRef.current?.value || "";
      setAddress(formatted);
    });

    // cleanup on unmount / step change
    return () => {
      if (listener && listener.remove) {
        listener.remove();
      }
    };
  }, [step]);

  // -------- Submitters with validation & error handling --------
  async function submitSepa() {
    setFormError("");
    setFieldErrors({});

    if (!recipient_name.trim()) {
      setFormError("Recipient name is required.");
      return;
    }
    if (!ibanValid) {
      setIbanMsg("❌ Please enter a valid IBAN before continuing.");
      return;
    }

    const payload = {
      // keep rail for XAPI
      rail: "SEPA",
      // and type for legacy/controller parity
      type: "SEPA",
      recipient_kind: recipientKind,
      recipient_name,
      iban,
      currency: effectiveCurrency(),
    };

    try {
      await apiRecipientCreate(payload);
      navigate("/recipients?flash=created");
    } catch (err: any) {
      const status = err?.status ?? err?.response?.status;
      const data = err?.data ?? err?.response?.data;
      if (status === 422 && data?.errors) {
        setFieldErrors(data.errors as ApiValidation);
        setFormError("Please correct the highlighted fields.");
      } else {
        setFormError(
          data?.message || "Failed to save recipient. Please try again."
        );
        console.error("SEPA recipient create failed", err);
      }
    }
  }

  function validBic(bic: string) {
    if (!bic) return true;
    const up = bic.toUpperCase();
    return up.length === 8 || up.length === 11;
  }

  async function submitSwift() {
    setFormError("");
    setFieldErrors({});

    if (!recipient_name.trim()) {
      setFormError("Recipient name is required.");
      return;
    }
    if (!bank_country) {
      setFormError("Bank country is required.");
      return;
    }
    if (!ibanOpt.trim() && !account_number.trim()) {
      setFormError("Provide either IBAN (optional) or Account Number.");
      return;
    }
    if (!validBic(bank_bic)) {
      setFormError("SWIFT/BIC must be 8 or 11 characters.");
      return;
    }
    if (ibanOpt.trim() && swiftIbanValid === false) {
      setFormError("Please enter a valid IBAN or leave the field empty.");
      return;
    }

    const payload = {
      // again: rail for XAPI, type for legacy controller
      rail: "SWIFT",
      type: "SWIFT",
      recipient_kind: recipientKind,
      recipient_name,
      full_address,
      iban: ibanOpt || null,
      account_number: account_number || null,
      routing_number: routing_number || null,
      bank_name: bank_name || null,
      bank_bic: bank_bic ? bank_bic.toUpperCase() : null,
      bank_address: bank_address || null,
      bank_country: bank_country.toUpperCase(),
      currency: effectiveCurrency(),
      intermediary_bank_name: intermediary_bank_name || null,
      intermediary_bank_bic: intermediary_bank_bic
        ? intermediary_bank_bic.toUpperCase()
        : null,
    };

    try {
      await apiRecipientCreate(payload);
      navigate("/recipients?flash=created");
    } catch (err: any) {
      const status = err?.status ?? err?.response?.status;
      const data = err?.data ?? err?.response?.data;
      if (status === 422 && data?.errors) {
        setFieldErrors(data.errors as ApiValidation);
        setFormError("Please correct the highlighted fields.");
      } else {
        setFormError(
          data?.message || "Failed to save recipient. Please try again."
        );
        console.error("SWIFT recipient create failed", err);
      }
    }
  }

  const sepaDisabled = !recipient_name.trim() || !ibanValid;
  const swiftDisabled =
    !recipient_name.trim() ||
    !bank_country ||
    (!ibanOpt.trim() && !account_number.trim()) ||
    !validBic(bank_bic) ||
    (ibanOpt.trim() && swiftIbanValid === false);

  return (
    // pb-28 ensures the page has breathing room above the fixed bottom tab bar
    <div className="max-w-2xl mx-auto w-full space-y-6 pb-28">
      {step === "select" && (
        <div className="grid gap-6 md:grid-cols-2">
          <div className="bg-white dark:bg-gray-800 border rounded-2xl p-6 shadow-sm">
            <h3 className="font-semibold mb-3">Recipient kind</h3>
            <div className="space-y-2 text-sm">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="recipient_kind"
                  checked={recipientKind === "individual"}
                  onChange={() => setRecipientKind("individual")}
                />
                <span>Individual</span>
              </label>
              <label className="inline-flex items-center gap-2 ml-6">
                <input
                  type="radio"
                  name="recipient_kind"
                  checked={recipientKind === "business"}
                  onChange={() => setRecipientKind("business")}
                />
                <span>Business</span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2">Type of Recipient.</p>
          </div>

          <div className="bg-white dark:bg-gray-800 border rounded-2xl p-6 shadow-sm">
            <h3 className="font-semibold mb-3">Choose transfer rail</h3>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setStep("sepa")}
                className="btn btn-white"
              >
                SEPA Recipient
              </button>
              <button
                type="button"
                onClick={() => setStep("swift")}
                className="btn btn-white"
              >
                SWIFT Recipient
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              We’ll carry your selected kind into the next step.
            </p>
          </div>
        </div>
      )}

      {step !== "select" && (
        <div className="section-box p-6 space-y-6 rounded-2xl shadow-sm border border-gray-200 bg-white">
          <button
            type="button"
            className="btn btn-white text-sm"
            onClick={() => setStep("select")}
          >
            ← Back
          </button>

          <div className="text-xs text-gray-500">
            Recipient kind:&nbsp;
            <span className="font-medium uppercase">{recipientKind}</span>
          </div>

          {formError && (
            <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
              {formError}
            </div>
          )}

          <div className={fieldWrap}>
            <label className={labelCls}>
              Recipient Name <span className="text-red-500">*</span>
            </label>
            <input
              className={inputCls}
              value={recipient_name}
              onChange={(e) => setName(e.target.value)}
              aria-invalid={!!fieldErrors.recipient_name}
            />
            {fieldErrors.recipient_name?.length ? (
              <p className="text-sm text-red-600">
                {fieldErrors.recipient_name[0]}
              </p>
            ) : null}
          </div>

          {/* Currency is locked to the platform default (e.g. EUR) */}
          <div className={fieldWrap}>
            <label className={labelCls}>Currency</label>
            <div className="py-2.5 px-3 rounded-lg bg-gray-50 border border-gray-200 inline-block">
              {effectiveCurrency()}
            </div>
            {/* hidden input to mirror Blade's hidden field behavior */}
            <input type="hidden" value={effectiveCurrency()} />
          </div>

          {step === "sepa" && (
            <>
              <div className={fieldWrap}>
                <label className={labelCls}>
                  IBAN <span className="text-red-500">*</span>
                </label>
                <input
                  className={inputCls}
                  value={iban}
                  onChange={(e) => setIban(e.target.value)}
                  onBlur={checkIban}
                  aria-invalid={!!fieldErrors.iban}
                />
                {!!iban && (
                  <p
                    className={`text-sm ${
                      ibanValid ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {ibanMsg}
                  </p>
                )}
                {fieldErrors.iban?.length ? (
                  <p className="text-sm text-red-600">
                    {fieldErrors.iban[0]}
                  </p>
                ) : null}
              </div>

              {/* Sticky action bar so the button never hides behind the tab bar */}
              <div className="sticky bottom-0 -mx-6 px-6 pt-3">
                <div className="bg-white/95 backdrop-blur border-t border-gray-200 py-3 flex justify-end rounded-b-2xl">
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={submitSepa}
                    disabled={sepaDisabled}
                  >
                    Continue
                  </button>
                </div>
              </div>
            </>
          )}

          {step === "swift" && (
            <>
              <div className={fieldWrap}>
                <label className={labelCls}>Recipient Full Address</label>
                <input
                  className={inputCls}
                  ref={addressInputRef}
                  value={full_address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Start typing an address..."
                  aria-invalid={!!fieldErrors.full_address}
                />
                {fieldErrors.full_address?.length ? (
                  <p className="text-sm text-red-600">
                    {fieldErrors.full_address[0]}
                  </p>
                ) : null}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className={fieldWrap}>
                  <label className={labelCls}>IBAN (optional)</label>
                  <input
                    className={inputCls}
                    value={ibanOpt}
                    onChange={(e) => setIbanOpt(e.target.value)}
                    onBlur={checkSwiftIban}
                    aria-invalid={!!fieldErrors.iban}
                  />
                  {!!ibanOpt && (
                    <p
                      className={`text-sm ${
                        swiftIbanValid ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {swiftIbanMsg}
                    </p>
                  )}
                  {fieldErrors.iban?.length ? (
                    <p className="text-sm text-red-600">
                      {fieldErrors.iban[0]}
                    </p>
                  ) : null}
                </div>
                <div className={fieldWrap}>
                  <label className={labelCls}>Account Number</label>
                  <input
                    className={inputCls}
                    value={account_number}
                    onChange={(e) => setAccount(e.target.value)}
                    aria-invalid={!!fieldErrors.account_number}
                  />
                  {fieldErrors.account_number?.length ? (
                    <p className="text-sm text-red-600">
                      {fieldErrors.account_number[0]}
                    </p>
                  ) : null}
                </div>
                <div className={fieldWrap}>
                  <label className={labelCls}>SWIFT/BIC</label>
                  <input
                    className={inputCls}
                    value={bank_bic}
                    onChange={(e) => setBic(e.target.value.toUpperCase())}
                    aria-invalid={!!fieldErrors.bank_bic}
                  />
                  {fieldErrors.bank_bic?.length ? (
                    <p className="text-sm text-red-600">
                      {fieldErrors.bank_bic[0]}
                    </p>
                  ) : null}
                </div>
                <div className={fieldWrap}>
                  <label className={labelCls}>Routing Number</label>
                  <input
                    className={inputCls}
                    value={routing_number}
                    onChange={(e) => setRouting(e.target.value)}
                    aria-invalid={!!fieldErrors.routing_number}
                  />
                  {fieldErrors.routing_number?.length ? (
                    <p className="text-sm text-red-600">
                      {fieldErrors.routing_number[0]}
                    </p>
                  ) : null}
                </div>
                <div className={fieldWrap}>
                  <label className={labelCls}>Bank Name</label>
                  <input
                    className={inputCls}
                    value={bank_name}
                    onChange={(e) => setBankName(e.target.value)}
                    aria-invalid={!!fieldErrors.bank_name}
                  />
                  {fieldErrors.bank_name?.length ? (
                    <p className="text-sm text-red-600">
                      {fieldErrors.bank_name[0]}
                    </p>
                  ) : null}
                </div>
                <div className={fieldWrap}>
                  <label className={labelCls}>Bank Address</label>
                  <input
                    className={inputCls}
                    value={bank_address}
                    onChange={(e) => setBankAddress(e.target.value)}
                    aria-invalid={!!fieldErrors.bank_address}
                  />
                  {fieldErrors.bank_address?.length ? (
                    <p className="text-sm text-red-600">
                      {fieldErrors.bank_address[0]}
                    </p>
                  ) : null}
                </div>

                {/* Country dropdown from config/countries */}
                <div className={fieldWrap}>
                  <label className={labelCls}>
                    Bank Country <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={inputCls}
                    value={bank_country}
                    onChange={(e) => setBankCountry(e.target.value)}
                    aria-invalid={!!fieldErrors.bank_country}
                  >
                    <option value="">Select a country...</option>
                    {countries.map((c) => (
                      <option key={c.iso} value={c.iso}>
                        {c.emoji ? `${c.emoji} ` : ""}
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.bank_country?.length ? (
                    <p className="text-sm text-red-600">
                      {fieldErrors.bank_country[0]}
                    </p>
                  ) : null}
                </div>

                <div className={fieldWrap}>
                  <label className={labelCls}>Intermediary Bank Name</label>
                  <input
                    className={inputCls}
                    value={intermediary_bank_name}
                    onChange={(e) => setInterName(e.target.value)}
                    aria-invalid={!!fieldErrors.intermediary_bank_name}
                  />
                  {fieldErrors.intermediary_bank_name?.length ? (
                    <p className="text-sm text-red-600">
                      {fieldErrors.intermediary_bank_name[0]}
                    </p>
                  ) : null}
                </div>
                <div className={fieldWrap}>
                  <label className={labelCls}>Intermediary Bank BIC</label>
                  <input
                    className={inputCls}
                    value={intermediary_bank_bic}
                    onChange={(e) => setInterBic(e.target.value.toUpperCase())}
                    aria-invalid={!!fieldErrors.intermediary_bank_bic}
                  />
                  {fieldErrors.intermediary_bank_bic?.length ? (
                    <p className="text-sm text-red-600">
                      {fieldErrors.intermediary_bank_bic[0]}
                    </p>
                  ) : null}
                </div>
              </div>

              {/* Sticky action bar so Save stays visible above the bottom nav */}
              <div className="sticky bottom-0 -mx-6 px-6 pt-3">
                <div className="bg-white/95 backdrop-blur border-t border-gray-200 py-3 flex justify-end rounded-b-2xl">
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={submitSwift}
                    disabled={swiftDisabled}
                  >
                    Save
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
